// Remove trailing slash from backend URL if present
const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = `${backendUrl}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || 'An error occurred');
  }
  return response.json();
};

// Public API
export const publicApi = {
  getCategories: async () => {
    const response = await fetch(`${API_URL}/categories`);
    return handleResponse(response);
  },
  
  getProjects: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.category) searchParams.append('category', params.category);
    if (params.featured !== undefined) searchParams.append('featured', params.featured);
    const response = await fetch(`${API_URL}/projects?${searchParams}`);
    return handleResponse(response);
  },
  
  getProject: async (id) => {
    const response = await fetch(`${API_URL}/projects/${id}`);
    return handleResponse(response);
  },
  
  getTestimonials: async () => {
    const response = await fetch(`${API_URL}/testimonials`);
    return handleResponse(response);
  },
  
  getClientLogos: async () => {
    const response = await fetch(`${API_URL}/client-logos`);
    return handleResponse(response);
  },
  
  getSettings: async () => {
    const response = await fetch(`${API_URL}/settings`);
    return handleResponse(response);
  },
};

// Admin API
export const adminApi = {
  // Categories
  createCategory: async (data) => {
    const response = await fetch(`${API_URL}/admin/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  updateCategory: async (id, data) => {
    const response = await fetch(`${API_URL}/admin/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  deleteCategory: async (id) => {
    const response = await fetch(`${API_URL}/admin/categories/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  // Projects
  getProjects: async () => {
    const response = await fetch(`${API_URL}/admin/projects`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  createProject: async (data) => {
    const response = await fetch(`${API_URL}/admin/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  updateProject: async (id, data) => {
    const response = await fetch(`${API_URL}/admin/projects/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  deleteProject: async (id) => {
    const response = await fetch(`${API_URL}/admin/projects/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  // Testimonials
  createTestimonial: async (data) => {
    const response = await fetch(`${API_URL}/admin/testimonials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  updateTestimonial: async (id, data) => {
    const response = await fetch(`${API_URL}/admin/testimonials/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  deleteTestimonial: async (id) => {
    const response = await fetch(`${API_URL}/admin/testimonials/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  // Client Logos
  createClientLogo: async (data) => {
    const response = await fetch(`${API_URL}/admin/client-logos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  updateClientLogo: async (id, data) => {
    const response = await fetch(`${API_URL}/admin/client-logos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  deleteClientLogo: async (id) => {
    const response = await fetch(`${API_URL}/admin/client-logos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  // Admins
  getAdmins: async () => {
    const response = await fetch(`${API_URL}/admin/admins`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  createAdmin: async (data) => {
    const response = await fetch(`${API_URL}/admin/admins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  
  deleteAdmin: async (id) => {
    const response = await fetch(`${API_URL}/admin/admins/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  // Settings
  getSettings: async () => {
    const response = await fetch(`${API_URL}/admin/settings`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  
  updateSettings: async (data) => {
    const response = await fetch(`${API_URL}/admin/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // File Upload
  uploadFile: async (file, folder = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/admin/upload?folder=${folder}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response);
  },

  uploadMultipleFiles: async (files, folder = 'general') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await fetch(`${API_URL}/admin/upload/multiple?folder=${folder}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return handleResponse(response);
  },
};
